<dw-router-parent>
  <ng-template>
    <div class="configured-dashboard-page">
      <app-report-dashboard *ngIf="incomingTile"
        [definition]="incomingTile.definition"
        [initialTileSelection]="incomingTile.selection"
        [showHeader]="false" [showFilterBar]="false"
        (drillDownClosed)="closeIncomingTile()">
      </app-report-dashboard>
      <div class="dashboard-config-loading" *ngIf="loading">
        <i class="fa fa-spinner fa-spin"></i> Loading dashboard configuration...
      </div>

      <div class="alert alert-danger m-3" *ngIf="errorMessage">{{ errorMessage }}</div>

      <ng-container *ngIf="definition && !errorMessage && !incomingTile">
        <header class="dashboard-group-header" *ngIf="showGroupNavigation">
          <div class="dashboard-group-heading">
            <div class="dashboard-group-icon" *ngIf="groupDefinition.iconClass">
              <i [class]="groupDefinition.iconClass"></i>
            </div>
            <div class="dashboard-group-copy">
              <h1>{{ groupTitle }}</h1>
              <p *ngIf="groupDefinition.description">{{ groupDefinition.description }}</p>
            </div>
          </div>
          <div class="dashboard-group-actions">
            <ng-container *ngIf="!designMode">
              <button *ngIf="canEditDashboard" type="button" class="btn btn-secondary"
                [disabled]="designerLoading" (click)="editDashboard()">
                <i class="fa" [class.fa-pencil]="!designerLoading" [class.fa-spinner]="designerLoading"
                  [class.fa-spin]="designerLoading"></i>
                {{ designerLoading ? 'Opening...' : 'Edit' }}
              </button>
              <button *ngIf="definition.allowPrint !== false" type="button"
                class="btn btn-success dashboard-group-print" (click)="printReport()">
                <i class="fa fa-print"></i> Print / Save PDF
              </button>
            </ng-container>
            <ng-container *ngIf="designMode">
              <button type="button" class="btn btn-outline-secondary"
                [disabled]="designerGroupSaving || designerSaving" (click)="manageDashboardItems()">
                <i class="fa" [class.fa-list-ol]="!designerGroupSaving"
                  [class.fa-spinner]="designerGroupSaving" [class.fa-spin]="designerGroupSaving"></i>
                Manage group
              </button>
              <span class="designer-unsaved-state" *ngIf="designerDirty">Unsaved changes</span>
              <button type="button" class="btn btn-outline-secondary" [disabled]="designerSaving || designerGroupSaving"
                (click)="cancelDashboardDesign()">Cancel</button>
              <button type="button" class="btn btn-primary"
                [disabled]="designerSaving || designerGroupSaving || !designerDirty"
                (click)="saveDashboard()">
                <i class="fa" [class.fa-save]="!designerSaving" [class.fa-spinner]="designerSaving"
                  [class.fa-spin]="designerSaving"></i>
                {{ designerSaving ? 'Saving...' : 'Save' }}
              </button>
            </ng-container>
          </div>
        </header>

        <div class="dashboard-navigation-row" *ngIf="showGroupNavigation">
          <dw-pills class="dashboard-tabs"
            [items]="displayedDashboardNavigationItems"
            [ngModel]="activeDashboardKey"
            valueField="dashboardKey"
            displayField="display"
            (ngModelChange)="selectTabByKey($event)">
          </dw-pills>
        </div>

        <div class="alert alert-danger designer-error" *ngIf="designerErrorMessage">
          {{ designerErrorMessage }}
        </div>

        <section class="inline-dashboard-designer" *ngIf="designMode && workingDefinition">
          <header class="dashboard-group-header inline-designer-header" *ngIf="!showGroupNavigation">
            <div>
              <span class="designer-eyebrow">Editing dashboard</span>
              <h1>{{ designerTitle }}</h1>
            </div>
            <div class="dashboard-group-actions">
              <button *ngIf="groupDefinition" type="button" class="btn btn-outline-secondary"
                [disabled]="designerGroupSaving || designerSaving" (click)="manageDashboardItems()">
                <i class="fa" [class.fa-list-ol]="!designerGroupSaving"
                  [class.fa-spinner]="designerGroupSaving" [class.fa-spin]="designerGroupSaving"></i>
                Manage group
              </button>
              <span class="designer-unsaved-state" *ngIf="designerDirty">Unsaved changes</span>
              <button type="button" class="btn btn-outline-secondary" [disabled]="designerSaving || designerGroupSaving"
                (click)="cancelDashboardDesign()">Cancel</button>
              <button type="button" class="btn btn-primary"
                [disabled]="designerSaving || designerGroupSaving || !designerDirty"
                (click)="saveDashboard()">
                <i class="fa" [class.fa-save]="!designerSaving" [class.fa-spinner]="designerSaving"
                  [class.fa-spin]="designerSaving"></i>
                {{ designerSaving ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </header>

          <div class="inline-designer-mode-tabs">
            <button type="button" [class.active]="designerEditorMode === 'design'"
              (click)="selectDesignerEditorMode('design')">
              <i class="fa fa-th-large"></i> Design
            </button>
            <button type="button" [class.active]="designerEditorMode === 'json'"
              (click)="selectDesignerEditorMode('json')">
              <i class="fa fa-code"></i> JSON
            </button>
          </div>

          <app-report-dashboard-design-canvas
            *ngIf="designerEditorMode === 'design'"
            [definition]="workingDefinition"
            [reusableWidgetRecords]="reusableWidgetRecords"
            [previewFilters]="designerPreviewFilters"
            [expressionContext]="expressionContext"
            [contextNames]="groupDefinition?.contextNames || []"
            (definitionChange)="onDesignerDefinitionChanged($event)"
            (jsonRequested)="selectDesignerEditorMode('json')">
          </app-report-dashboard-design-canvas>

          <label class="inline-json-editor" *ngIf="designerEditorMode === 'json'">
            <span>Dashboard configuration JSON</span>
            <textarea class="form-control" rows="30" spellcheck="false"
              [(ngModel)]="workingConfigText" (ngModelChange)="onWorkingConfigTextChanged()"></textarea>
          </label>
        </section>

        <app-report-dashboard #dashboardComponent *ngIf="!designMode"
          [definition]="definition"
          [filterPersistenceGroupKey]="groupDefinition?.key"
          [groupFilterSession]="groupFilterSession"
          [expressionContext]="expressionContext"
          [showHeader]="showDashboardHeader"
          [showDashboardTitle]="showGroupNavigation"
          [canEditDashboard]="canEditDashboard"
          [showChartGridFirst]="showChartGridFirstByDefault"
          (editDashboardRequested)="editDashboard()"
          (dashboardDefaultsChanged)="onDashboardDefaultsChanged($event)"
          (showChartGridFirstChange)="onShowChartGridFirstByDefaultChanged($event)">
        </app-report-dashboard>
      </ng-container>
    </div>
  </ng-template>
</dw-router-parent>
